import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import ListFilters from '@/common/components/ListFilters'
import ListHeader from '@/common/components/ListHeader'
import Table from '@/common/components/Table'
import TextField from '@/common/components/TextField'
import { DatePickerWithRange } from '@/common/components/ui/dateRangePicker'
import { billingBillStatus } from '@/common/constants/statusBills.constants'
import useDebounce from '@/common/hooks/useDebounce'
import { useFilterDate } from '@/common/hooks/useFilterDate'
import { useFilterParams } from '@/common/hooks/useFilterParams'
import { Mapper } from '@/common/services/mapper'
import { Option } from '@/common/types/option'
import { CompaniesTransformer } from '@/features/companies/services/companiesTransformer'
import { defaultColumns } from '@/features/establishmentBills/constants/establishmentBills.constant'
import { useEstablishmentBills } from '@/features/establishmentBills/hooks/useEstablishmentBills'
import { EstablishmentBillsTransformer } from '@/features/establishmentBills/services/establishmentBillsTransformer'
import {
  EstablishmentBillData,
  EstablishmentBillTableItem,
} from '@/features/establishmentBills/types/establishmentBills.type'
import { useEstablishmentCompanies } from '@/features/establishments/hooks/useEstablishmentCompanies'
import { ArrowDownToLineIcon, SearchIcon } from 'lucide-react'

const List = () => {
  const { filterParams, setFilterParams } = useFilterParams({
    status: billingBillStatus.find(({ value }) => value === 'pending_payment')
  });
  const { date, formattedDate } = useFilterDate(filterParams)

  const [search, setSearch] = useDebounce(filterParams.search || '')
  const [searchEstablishment, setSearchEstablishment] = useDebounce('')

  const {
    data: establishmentBills,
    meta,
    isPending: isPendingEstablishmentBills,
    error: establishmentBillsError = {} as any,
    refetch: refetchEstablishmentBills,
  } = useEstablishmentBills<EstablishmentBillTableItem[]>({
    filters: {
      search: search,
      page: filterParams.page,
      establishmentId: filterParams.establishment?.value,
      status: filterParams.status?.value,
      startDate: formattedDate.from,
      endDate: formattedDate.to,
    },
    mapper: Mapper.mapWithTransformer<EstablishmentBillData[], EstablishmentBillTableItem[]>({
      transformer: EstablishmentBillsTransformer.tableTransformer(),
    }),
  })

  const { data: establishmentOptions } = useEstablishmentCompanies({
    filters: {
      search: searchEstablishment,
    },
    mapper: Mapper.mapWithTransformer({ transformer: CompaniesTransformer.optionsTransformer() }),
  })

  return (
    <div className="flex flex-col gap-6">
      <ListHeader Icon={<ArrowDownToLineIcon className="size-10" strokeWidth={1} />} title="Faturas (A Pagar)" />

      <ErrorMessageWrapper
        className="h-[calc(100vh-128px)]"
        statusCode={establishmentBillsError?.status}
        refetchFunction={refetchEstablishmentBills}
      >
        <div className="flex flex-col gap-6">
          <TextField
            LeftIcon={<SearchIcon />}
            placeholder="Pesquisar"
            className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
            onChange={(e) => {
              setSearch((e.target as HTMLInputElement).value)
              setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }))
            }}
            value={filterParams.search}
          />

          <div className="flex items-center gap-2">
            <DatePickerWithRange date={date} setDate={(date) => setFilterParams((params) => ({ ...params, date }))} />

            <ListFilters
              filters={[
                {
                  className: 'w-56',
                  onChange: (status) => setFilterParams((prev) => ({ ...prev, status })),
                  value: filterParams.status,
                  options: [{ label: 'Todas', value: '' }, ...(billingBillStatus || [])],
                  placeholder: 'Status',
                  dataTestid: 'status',
                },
                {
                  className: 'w-72',
                  onChange: (establishment) => setFilterParams((prev) => ({ ...prev, establishment })),
                  value: filterParams.establishment,
                  onInputChange: (value) => setSearchEstablishment(value),
                  options: [{ label: 'Todas', value: '' }, ...((establishmentOptions || []) as Option[])],
                  optionClassName: 'whitespace-pre overflow-hidden',
                  placeholder: 'Empresa',
                },
              ]}
            />
          </div>

          <Table
            className="max-h-[calc(100vh-260px)]"
            columns={defaultColumns}
            isLoadingData={isPendingEstablishmentBills}
            rows={establishmentBills}
            totalPages={meta.total_pages}
            totalCount={meta.total}
            hasPagination
            rowEmptyValue="--"
            page={filterParams.page ? Number(filterParams.page) : 1}
            setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
          />
        </div>
      </ErrorMessageWrapper>
    </div>
  )
}

export default List
